import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants/src';
import { AppUI, ExerciseUI } from '@seedlang/state';
import { CampaignStore, CustomTabStore, GridItemWorksheetStore, WorksheetCustomTabStore, WorksheetStore } from '@seedlang/stores';
import { isBlank, isPresent } from '@seedlang/utils';
import autobind from 'autobind-decorator';
import Alert from 'components/alert';
import DeleteButton from 'components/button/delete_button';
import CheckIfTrue from 'components/check_if_true';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import InPlaceDatePicker from 'components/form/in_place_date_picker';
import InPlaceImageUpload from 'components/form/in_place_image_upload';
import InPlaceText from 'components/form/in_place_text';
import GridItemSearch from 'components/grid_item_search';
import InfoTooltip from 'components/info_tooltip';
import Modal from 'components/modal';
import PodcastEpisodeSearch from 'components/podcast_episode_search';
import Spinner from 'components/spinner';
import Text from 'components/text';
import Tags from 'components/worksheet/tags';
import { autorun, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import DownloadLinkEdit from 'pages/creator/download_links/download_link_edit';
import ExerciseCreate from 'pages/creator/exercises/exercise_create';
import ExerciseIndex from 'pages/creator/exercises/exercise_index';
import TagEdit from 'pages/creator/tags/tag_edit';
import React from 'react';
import { Link } from 'react-router';
import ReactTooltip from 'react-tooltip';
import BackEndExplainer from '../back_end_explainer';
import { find } from 'lodash';
import Grid from 'components/layout/grid';
import Stack from 'components/layout/stack';
import GrayButton from 'components/button/gray_button';
import PageTitle from 'components/common/page_title';

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TableText = styled.div`
  text-transform: uppercase;
  display: flex;
`;

const PodcastEpisode = styled.div`
  display: flex;
  margin-top: 5px;
  padding: 5px;
  background: #FFF;
  font-size: 16px;
`;

const Title = styled.div`
  flex: 1;
`;

const InvalidIconWrapper = styled.div`
  text-transform: none;
  margin: ${props => props.margin};
`;

const NameDetails = styled.div`
  display: flex;
  justify-content: space-between;
`;

const DatePickerRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const ImageWrapper = styled.div`
  position: relative;
  display: inline-block;
  .info-tooltip {
    position: absolute;
    left: 0;
    background: green;
    color: white;
    width: 25px;
    height: 25px;
    text-align: center;
    line-height: 24px;
    border-radius: 50%;
    font-size: 14px;
  }
  .fa-times {
    position: absolute;
    right: 0;
    background: red;
    color: white;
    width: 25px;
    height: 25px;
    text-align: center;
    line-height: 24px;
    border-radius: 50%;
    font-size: 14px;
    cursor: pointer;
  }
  img {
    margin-top: 10px;
    max-width: 180px;
  }
`;

const Documentation = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px; 
  margin: 5px 0;
  background: #e4e4e4;
  padding: 5px 10px;
  border-radius: 5px;
  span {
    white-space: pre;
  }
  i {
    margin-right: 10px;
  }
`;

@observer
class WorksheetEdit extends React.Component {
  @observable editTags = false;
  @observable editDownloadLinks = false;
  @observable showPublicationAlert = false;
  @observable showNotificationAlert = false;
  @observable showEmailTestMessage = false;
  @observable showEmailTestMessage = false;
  @observable showEmailTestSpinner = false;
  @observable creatingCustomTab = false;
  @observable showAdvancedSettings = false;
  @observable generatedDownloadLinks = {
    csv: { link: null, linkType: null, generating: false, showConfirmation: false, showSlowWarning: false, askForConfirm: false },
    pdf: { link: null, linkType: null, generating: false, showConfirmation: false, showSlowWarning: false, askForConfirm: false },
  };

  constructor(props) {
    super(props);
    this.getWorksheet();
    AppUI.downloadLinkTypeStore.getIndex();
    CustomTabStore.getIndex();
    let loaded = false;

    autorun(() => {
      if (AppUI.downloadLinkTypeStore.hasIndexData && WorksheetStore.hasShowData && !loaded) {
        loaded = true;
        this.generatedDownloadLinks.pdf.linkType = AppUI.downloadLinkTypeStore.indexData.find(item => item.generatedPdfs);
        this.generatedDownloadLinks.csv.linkType = AppUI.downloadLinkTypeStore.indexData.find(item => item.generatedCsvs);
        for (const fileType of ['csv', 'pdf']) {
          if (isPresent(this.generatedDownloadLinks[fileType].linkType)) {
            this.generatedDownloadLinks[fileType].link = WorksheetStore.showData.downloadLinks.find(item => item.downloadLinkTypeId === this.generatedDownloadLinks[fileType].linkType.id);
          }
        }
      }
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.params?.worksheetId !== this.props.params?.worksheetId) {
      this.getWorksheet();
    }
  }

  @autobind getWorksheet() {
    WorksheetStore.getShow({ids: {worksheetId: this.props.params.worksheetId}, queryStrings: {expanded: true}}, this.afterGetShow);
  }

  @autobind afterGetShow(resp) {
    WorksheetStore.setShowData(resp);
    for (const fileType of ['csv', 'pdf']) {
      if (this.generatedDownloadLinks[fileType].generating) {
        this.generatedDownloadLinks[fileType].generating = false;
        this.generatedDownloadLinks[fileType].link = null;
        if (isPresent(this.generatedDownloadLinks[fileType].link)) {
          this.generatedDownloadLinks[fileType].showConfirmation = true;
        } else {
          this.generatedDownloadLinks[fileType].showSlowWarning = true;
        }
      }
    }
  }

  @autobind onAddGridItem(gridItem) {
    GridItemWorksheetStore.create({data: {gridItemId: gridItem.id, worksheetId: this.props.params.worksheetId}}, this.getWorksheet);
  }

  @autobind onRemoveGridItem(id) {
    WorksheetStore.removeGridItem({data: {grid_item_id: id, worksheet_id: this.props.params.worksheetId}}, this.getWorksheet);
  }

  @autobind addPodcastEpisode(podcastEpisode) {
    WorksheetStore.update({data: {podcastEpisodeId: podcastEpisode.id}, ids: {worksheetId: this.props.params.worksheetId}}, this.getWorksheet);
  }

  @autobind clearPodcastEpisode() {
    WorksheetStore.update({data: {podcastEpisodeId: null}, ids: {worksheetId: this.props.params.worksheetId}}, this.getWorksheet);
  }

  republish() {
    WorksheetStore.update({data: {published: true}, ids: {worksheetId: this.props.params.worksheetId}});
  }

  @autobind generateDownloadLink(fileType) {
    this.generatedDownloadLinks[fileType].generating = true;
    this.generatedDownloadLinks[fileType].askForConfirm = false;
    if (isPresent(this.generatedDownloadLinks[fileType].link)) {
      this.generatedDownloadLinks[fileType].link.set('text', '');
    }

    const successCallback = (() => {
      this.getWorksheet();
    });

    const failureCallback = (() => {
      this.generatedDownloadLinks[fileType].generating = false;
    });

    const generateParams = {
      ids: {worksheetId: this.props.params.worksheetId},
      failureCallback,
    };

    if (fileType === 'csv') {
      WorksheetStore.generateCsvLink(generateParams, successCallback);
    } else {
      WorksheetStore.generatePdfLink(generateParams, successCallback);
    }

    setTimeout(() => {
      if (this.generatedDownloadLinks[fileType].generating) {
        this.generatedDownloadLinks[fileType].showSlowWarning = true;
      }
    }, 5000);
  }

  @autobind onClickGenerateDownloadLink(fileType) {
    if (isPresent(this.generatedDownloadLinks[fileType].link?.text) && isPresent(this.generatedDownloadLinks[fileType].linkType)) {
      this.generatedDownloadLinks[fileType].askForConfirm = true;
    } else {
      this.generateDownloadLink(fileType);
    }
  }

  @autobind afterUpdate(resp) {
    if (isPresent(resp) && resp.wasPublished) {
      this.showPublicationAlert = true;
      this.showNotificationAlert = resp.wasNotified;
    } else {
      this.showPublicationAlert = false;
      this.showNotificationAlert = false;
    }
  }

  @autobind toggleYoutubeThumbnail() {
    WorksheetStore.update({data: {hideYoutubeThumbnail: !WorksheetStore.showData.hideYoutubeThumbnail}, ids: {worksheetId: this.props.params.worksheetId}}, this.getWorksheet);
  }

  @autobind onSendEmailTest() {
    this.showEmailTestSpinner = true;
    if (WorksheetStore.hasShowData && isPresent(WorksheetStore.showData.campaign)) {
      CampaignStore.sendCampaign({data: {test_sending: true}, ids: {campaignId: WorksheetStore.showData.campaign?.id}}, resp => this.afterSendCampaign(resp));
    }
  }

  @autobind afterSendCampaign() {
    this.showEmailTestMessage = true;
    this.showEmailTestSpinner = false;
    setTimeout(() => this.showEmailTestMessage = false, 6000);
  }

  @computed get campaign() {
    if (!WorksheetStore.hasShowData) { return; }
    return WorksheetStore.showData.campaign;
  }

  @autobind createWorksheetCustomTab(customTabId) {
    if (isBlank(customTabId)) { return; }
    this.creatingCustomTab = true;
    WorksheetCustomTabStore.create(
      {
        data: {
          custom_tab_id: customTabId,
          worksheet_id: this.props.params.worksheetId,
        },
      }, this.afterCreateCustomTab);
  }

  @autobind afterCreateCustomTab(resp) {
    this.creatingCustomTab = false;
    this.getWorksheet();
    AppUI.routeStore.routeToNamed('creator.worksheets_custom_tabs.edit', { worksheetId: this.props.params.worksheetId, worksheetCustomTabId: resp.id });
  }

  @autobind worksheetCustomTab(customTabId) {
    return find(WorksheetStore.showData?.customTabs, { customTabId });
  }

  @autobind customTab(id) {
    return find(CustomTabStore.indexData, { id });
  }

  @autobind onDestroyWorksheetCustomTab(worksheetCustomTabId) {
    WorksheetCustomTabStore.destroy({ids: {worksheetCustomTabId}}, this.getWorksheet);
  }

  render() {
    return (
      <Stack gap='sm' alignItems='stretch'>
        <PageTitle>{WorksheetStore.showData?.name}</PageTitle>
        {
          this.editTags &&
            <Modal
              onCloseModal={() => this.editTags = false}
              height='550px'
              iconFill='#000'
            >
              <TagEdit
                groupId={AppUI.groupId}
                worksheet={WorksheetStore.showData}
                onChange={this.getWorksheet}
              />
            </Modal>
        }
        {
          WorksheetStore.hasShowData && this.editDownloadLinks &&
            <Modal
              onCloseModal={() => this.editDownloadLinks = false}
              height='550px'
              iconFill='#000'
            >
              <DownloadLinkEdit
                groupId={AppUI.groupId}
                worksheet={WorksheetStore.showData}
                onChange={this.getWorksheet}
              />
            </Modal>
        }
        <div className='worksheet-edit'>
          <div className='breadcrumbs'>
            <div>
              <Link to='creator.worksheets.index'>
                Posts
              </Link>
            </div>
            <i className='fa fa-angle-double-right' />
            <div className='current'>
              {WorksheetStore.showData.name}
            </div>
          </div>
          {
            AppUI.site.blockPostNotifications && !WorksheetStore.showData.published &&
              <Alert margin='5px 0'>{`User will not be notified upon publication, because all notifications are currently blocked. ${AppUI.user.canAccessAdminSite ? 'This can be modified in the site settings.' : ''}`}</Alert>
          }
          {
            this.showPublicationAlert &&
              <Alert background={Theme.green} margin='5px 0'>{`The post was just published${this.showNotificationAlert ? ' and users were notified' : '. Users were not notified.'}`}</Alert>
          }
          <Grid columns={[5,4,3]}>
            <fieldset style={{height: '100%'}}>
              <legend>Name</legend>
              <InPlaceText
                submitOnEnter
                defaultValue={WorksheetStore.showData.name}
                model='worksheets'
                field='name'
                id={WorksheetStore.showData.id}
                updateCallback={this.afterUpdate}
              />
              <NameDetails>
                <Tags
                  withEpisodeCode
                  worksheet={WorksheetStore.showData}
                />
                <InfoTooltip>
                  <div>Follow this format for video episode numbers:</div>
                  <div>"(SEG32)" at the end of the name</div>
                </InfoTooltip>
              </NameDetails>
              <GrayButton
                type='button'
                style={{height: '20px', marginTop: '10px'}}
                onClick={() => this.editTags = true}
              >
                <i className='fa fa-angle-right' />
                Edit Tags ({WorksheetStore.showData.tagsCount} Tag{WorksheetStore.showData.tagsCount === 1 ? '' : 's'})
              </GrayButton>
            </fieldset>
            <fieldset style={{height: '100%'}}>
              <legend>Settings</legend>
              <div
                style={{display: 'flex', justifyContent: 'space-between'}}
              >
                <span
                  data-tip
                  data-for='published'
                >
                  <CheckIfTrue
                    value={WorksheetStore.showData.published}
                    label='Published'
                    iconFontSize='18px'
                  />
                  {
                    (WorksheetStore.showData.publishedAt?.isFuture || isBlank(WorksheetStore.showData.publishedAt)) &&
                      <ReactTooltip
                        type='dark'
                        effect='solid'
                        id='published'
                        class='custom-tooltip'
                      >
                        {
                          !WorksheetStore.showData.published && WorksheetStore.showData.publishedAt?.isFuture &&
                            <div>{`This post will be published automatically ${WorksheetStore.showData.publishedAt.relativeTime}.`}</div>
                        }
                        {
                          !WorksheetStore.showData.published && isBlank(WorksheetStore.showData.publishedAt) &&
                            <div>Set a publication date to publish this post.</div>
                        }
                        {
                          WorksheetStore.showData.published &&
                            <div>To unpublish, delete the publication date or set it in the future.</div>
                        }
                      </ReactTooltip>
                  }
                </span>
                <div>
                  <InPlaceCheckbox
                    value={WorksheetStore.showData.pinned}
                    field='pinned'
                    model='worksheets'
                    id={WorksheetStore.showData.id}
                    afterChange={this.getWorksheet}
                  >
                    Pinned
                    <InfoTooltip>
                      <div>Pinned posts will be displayed at the top of the grid, </div>
                      <div>except in Grid Sections, which have their own order.</div>
                    </InfoTooltip>
                  </InPlaceCheckbox>
                </div>
              </div>
              <div>
                <InPlaceCheckbox
                  value={WorksheetStore.showData.accessWithoutMembership}
                  field='access_without_membership'
                  model='worksheets'
                  id={WorksheetStore.showData.id}
                  afterChange={this.getWorksheet}
                >
                  Can Access without Membership
                </InPlaceCheckbox>
              </div>
              {
                WorksheetStore.hasShowData &&
                <Link
                  to={{name: 'worksheets.show', params: {worksheetId: WorksheetStore.showData.id}}}
                  className='underline'
                  style={{fontSize: 12}}
                >
                  Go to Post
                </Link>
              }
            </fieldset>
            <fieldset style={{height: '100%'}}>
              <legend style={{marginBottom: '-15px'}}>Publication
                {
                  !WorksheetStore.showData.published &&
                    <InfoTooltip position='left'>
                      <div>The post will be immediately published if you set a <b>publication date</b> in the past.
                      </div>
                      <div>If the date is set in the future, the publication will be scheduled.</div>
                    </InfoTooltip>
                }
              </legend>
              {
                !AppUI.site.blockPostNotifications &&
                  <InPlaceCheckbox
                    value={WorksheetStore.showData.skipNotification}
                    field='skip_notification'
                    model='worksheets'
                    id={WorksheetStore.showData.id}
                    afterChange={this.getWorksheet}
                    disabled={WorksheetStore.showData.published}
                  >
                    <i className='fa fa-mail' />
                    Do Not Notify Members
                  </InPlaceCheckbox>
              }
              <DatePickerRow>
                <InPlaceDatePicker
                  withTime
                  label='Post publication date'
                  model='worksheets'
                  field='published_at'
                  id={WorksheetStore.showData.id}
                  value={WorksheetStore.showData.publishedAt}
                  afterChange={this.getWorksheet}
                  timeIntervals={60}
                  updateCallback={this.afterUpdate}
                />
                <InfoTooltip position='left'>This is the date and time when the post will be published (in your local time).</InfoTooltip>
                {
                  !WorksheetStore.showData.isValid &&
                    <InvalidIconWrapper
                      data-tip
                      data-for='valid-publish'
                    >
                      <i className='fa fa-warning' />
                      <ReactTooltip
                        type='dark'
                        effect='solid'
                        id='valid-publish'
                        class='custom-tooltip'
                        place='left'
                      >
                        <div>Look at the warning icons on the page</div>
                        <div>to make the post valid.</div>
                      </ReactTooltip>
                    </InvalidIconWrapper>
                }
                {
                  WorksheetStore.showData.isValid && WorksheetStore.showData.hasUnpublishedVocab &&
                    <InvalidIconWrapper
                      data-tip
                      data-for='valid-publish-vocab'
                    >
                      <i className='fa fa-warning' />
                      <ReactTooltip
                        type='dark'
                        effect='solid'
                        id='valid-publish-vocab'
                        class='custom-tooltip'
                        place='left'
                      >
                        <div>This post is valid but</div>
                        <div>has unpublished vocab ↓</div>
                      </ReactTooltip>
                    </InvalidIconWrapper>
                }
              </DatePickerRow>
              <DatePickerRow>
                <InPlaceDatePicker
                  label='Content publication date'
                  model='worksheets'
                  field='content_published_at'
                  id={WorksheetStore.showData.id}
                  value={WorksheetStore.showData.contentPublishedAt}
                  afterChange={this.getWorksheet}
                  updateCallback={this.afterUpdate}
                  isClearable={false}
                />
                <InfoTooltip position='left'>
                  <div>This is the original publication date of the video or podcast.</div>
                </InfoTooltip>
              </DatePickerRow>
              {
                !WorksheetStore.showData.isValid && !WorksheetStore.showData.published && WorksheetStore.showData.publishedAt?.isFuture &&
                  <Alert margin='5px 0'>{`If this post is still invalid on ${WorksheetStore.showData.publishedAt.formattedDate}, it will not be published.`}</Alert>
              }
              {
                !WorksheetStore.showData.isValid && !WorksheetStore.showData.published && WorksheetStore.showData.publishedAt?.isPast &&
                  <Alert margin='5px 0'>This post hasn't been published because it's invalid.</Alert>
              }
            </fieldset>
          </Grid>
          <Grid columns={2}>
            <fieldset>
              <legend>Youtube Episode</legend>
              <table>
                <tbody>
                  <tr>
                    <td width='130px'>
                      <TableText>
                        Thumbnail
                      </TableText>
                    </td>
                    <td>
                      <InPlaceImageUpload
                        model='images'
                        image={WorksheetStore.showData.image}
                        margin='0'
                        onChange={this.getWorksheet}
                        querystring={{
                          imageable_id: WorksheetStore.showData.id,
                          imageable_type: 'Worksheet',
                        }}
                        updateCallback={this.afterUpdate}
                      />
                      {
                        WorksheetStore.showData.youtubeUrlIsValid && WorksheetStore.showData.hideYoutubeThumbnail &&
                          <div style={{padding: '6.3px 10.5px'}}>
                            <button
                              type='button'
                              style={{height: '22px'}}
                              onClick={this.toggleYoutubeThumbnail}
                            >
                              Load from Youtube
                            </button>
                          </div>
                      }
                      {
                        WorksheetStore.showData.youtubeUrlIsValid && isPresent(WorksheetStore.showData.youtubeThumbnailUrl) && !WorksheetStore.showData.hideYoutubeThumbnail && isBlank(WorksheetStore.showData.image) &&
                          <ImageWrapper>
                            <i
                              className='fa fa-times'
                              onClick={this.toggleYoutubeThumbnail}
                            />
                            <InfoTooltip color='white'><div>This image is automatically retrieved from Youtube.</div><div>You can upload another file if you prefer.</div></InfoTooltip>
                            <img alt='' src={WorksheetStore.showData.youtubeThumbnailUrl} />
                          </ImageWrapper>
                      }
                    </td>
                  </tr>
                  <tr>
                    <td width='130px'>
                      <TableText>
                        Youtube URL
                        {
                          !WorksheetStore.showData.youtubeUrlIsValid && isBlank(WorksheetStore.showData.podcastEpisode) &&
                            <InvalidIconWrapper
                              data-tip
                              data-for='youtube-url'
                            >
                              <i className='fa fa-warning' />
                              <ReactTooltip
                                type='dark'
                                effect='solid'
                                id='youtube-url'
                                class='custom-tooltip'
                              >
                                The Youtube URL must start with 'https://www.youtube.com/'
                              </ReactTooltip>
                            </InvalidIconWrapper>
                        }
                      </TableText>
                    </td>
                    <td>
                      <InPlaceText
                        show
                        submitOnEnter
                        defaultValue={WorksheetStore.showData.youtubeUrl}
                        model='worksheets'
                        field='youtube_url'
                        id={WorksheetStore.showData.id}
                        afterChange={this.getWorksheet}
                        updateCallback={this.afterUpdate}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </fieldset>
            {
              isBlank(WorksheetStore.showData.youtubeThumbnailUrl) && isBlank(WorksheetStore.showData.image) &&
                <fieldset style={{height: '100%'}}>
                  <legend>Preview Text
                    <InfoTooltip>This will be used as a thumbnail if there is no thumbnail image.</InfoTooltip>
                    {
                      isBlank(WorksheetStore.showData.imageUrl) && isBlank(WorksheetStore.showData.previewText) &&
                      <InvalidIconWrapper
                        data-tip
                        data-for='preview-text'
                      >
                        <i className='fa fa-warning' />
                        <ReactTooltip
                          type='dark'
                          effect='solid'
                          id='preview-text'
                          class='custom-tooltip'
                        >
                          A post needs either a thumbnail or a preview text
                        </ReactTooltip>
                      </InvalidIconWrapper>
                    }
                  </legend>
                  <InPlaceText
                    maxLength={300}
                    defaultValue={WorksheetStore.showData.previewText}
                    show={isBlank(WorksheetStore.showData.imageUrl) && isBlank(WorksheetStore.showData.previewText)}
                    model='worksheets'
                    field='preview_text'
                    id={WorksheetStore.showData.id}
                    inputType='textarea'
                    afterChange={this.getWorksheet}
                  />
                </fieldset>
            }
          </Grid>
          <Grid columns={[3,1]}>
            <fieldset>
              <legend>Podcast Episode</legend>
              <PodcastEpisodeSearch
                onSubmit={podcastEpisode => this.addPodcastEpisode(podcastEpisode)}
              />
              {
                isPresent(WorksheetStore.showData.podcastEpisode) &&
                  <PodcastEpisode>
                    <Title>
                      <Link
                        to={{name: 'creator.podcast_episodes.edit', params: {podcastEpisodeId: WorksheetStore.showData.podcastEpisode.id}}}
                      >
                        {WorksheetStore.showData.podcastEpisode.title}
                      </Link>
                    </Title>
                    <div style={{width: '40px'}}>
                      <i
                        className='fa fa-times fa-on-click fa-block right'
                        onClick={this.clearPodcastEpisode}
                      />
                    </div>
                  </PodcastEpisode>
              }
            </fieldset>
            {
              AppUI.site.showWorksheetComments &&
                <fieldset>
                  <legend>Post Comments</legend>
                  <Row>
                    {
                      WorksheetStore.showData.hasForumThread &&
                      <InPlaceCheckbox
                        blockClassName
                        id={WorksheetStore.showData.forumThread.id}
                        value={WorksheetStore.showData.forumThread.hidden}
                        field='hidden'
                        model='forum_threads'
                        afterChange={this.getWorksheet}
                      >
                        Hide Comments
                      </InPlaceCheckbox>
                    }
                    {
                      WorksheetStore.showData.hasForumThread && WorksheetStore.showData.forumThread.hidden &&
                      <i className='fa fa-warning' />
                    }
                  </Row>
                  {
                    WorksheetStore.hasShowData &&
                    <Row>
                      <Link
                        to={{name: 'worksheets.comments.show', params: {worksheetId: WorksheetStore.showData?.id}}}
                        className='underline'
                        style={{fontSize: 12}}
                      >
                        Go to comments
                      </Link>
                    </Row>
                  }
                </fieldset>
            }
          </Grid>
          <fieldset>
            <legend>Video or Podcast description</legend>
            { isBlank(WorksheetStore.showData.description) && <i className='fa fa-warning' /> }
            <InPlaceText
              smallRichText
              show={isBlank(WorksheetStore.showData.description)}
              defaultValue={WorksheetStore.showData.description}
              model='worksheets'
              field='description'
              id={WorksheetStore.showData.id}
              afterChange={this.getWorksheet}
              maxHeight='250px'
              scrollable
              updateCallback={this.afterUpdate}
            />
          </fieldset>
          <fieldset>
            <legend>{`Vocab (${WorksheetStore.showData.worksheetWordsCount} words)`}</legend>
            {
              WorksheetStore.showData.hasUnpublishedVocab &&
                <Row>
                  <i className='fa fa-warning' />
                  <Text color='#ff5252' fontSize='14px' margin='0 10px'>This post has unpublished vocab.</Text>
                </Row>
            }
            <Row>
              <Link
                to={{name: 'creator.vocab.edit', params: {worksheetId: this.props.params.worksheetId}}}
              >
                <GrayButton
                  type='button'
                  style={{marginBottom: '10px'}}
                >
                  <i className='fa fa-angle-right' />
                  Edit Vocab
                </GrayButton>
              </Link>
            </Row>
          </fieldset>
          <fieldset>
            <legend>Exercises</legend>
            <ExerciseCreate
              withModal
              worksheetId={this.props.params.worksheetId}
              afterCreate={() => {
                this.getWorksheet();
                ExerciseUI.exerciseStore.reloadIndex();
              }}
            />
            <ExerciseIndex
              showPosition
              afterUpdate={this.getWorksheet}
              groupId={AppUI.groupId}
              worksheetId={this.props.params.worksheetId}
            />
          </fieldset>
          <fieldset>
            <legend>Downloads</legend>
            <Row>
              <GrayButton
                type='button'
                onClick={() => this.editDownloadLinks = true}
              >
                <i className='fa fa-angle-right' />
                Edit Download Links ({WorksheetStore.showData.downloadLinks?.length} Link{WorksheetStore.showData.downloadLinks?.length === 1 ? '' : 's'})
              </GrayButton>
              <GrayButton
                type='button'
                onClick={this.onClickGenerateDownloadLink.bind(this, 'pdf')}
                disabled={this.generatedDownloadLinks.pdf.generating}
              >
                {this.generatedDownloadLinks.pdf.generating ? 'Generating PDF...' : (
                  <>
                    <i className='fa fa-file-pdf-o' />
                    Generate PDF <InfoTooltip>This will generate a PDF of the post. It will be added to the post's downloads.</InfoTooltip>
                  </>
                )}
              </GrayButton>
              <GrayButton
                type='button'
                onClick={this.onClickGenerateDownloadLink.bind(this, 'csv')}
                disabled={this.generatedDownloadLinks.csv.generating}
              >
                {this.generatedDownloadLinks.csv.generating ? 'Generating Vocab CSV...' : (
                  <>
                    <i className='fa fa-file-text-o' />
                    Generate Vocab CSV <InfoTooltip>This will generate a CSV of the post's vocabulary that can be imported into Anki or Quizlet. It will be added to the post's downloads.</InfoTooltip>
                  </>
                )}
              </GrayButton>
              <GrayButton
                as='a'
                href={`/api/worksheets/${this.props.params.worksheetId}/export`}
                target='_blank'
                type='button'
                rel='noreferrer'
              >
                <i className='fa fa-download' />
                Export Post
                <InfoTooltip>
                  <div>This will export the post data in json and csv formats</div>
                  <div>so that it can be reuploaded to the platform in case of data loss.</div>
                </InfoTooltip>
              </GrayButton>
            </Row>
            {['csv', 'pdf'].map(fileType =>
              <React.Fragment key={fileType}>
                {
                  this.generatedDownloadLinks[fileType].askForConfirm && isPresent(this.generatedDownloadLinks[fileType].linkType) && isPresent(this.generatedDownloadLinks[fileType].link) &&
                  <Alert
                    showCloseButton
                    onClose={() => this.generatedDownloadLinks[fileType].askForConfirm = false}
                  >
                    <div>{`The Download Link \'${this.generatedDownloadLinks[fileType].linkType.title}\' already has a link. Do you want to replace it with the newly generated ${fileType.toUpperCase()}?`}</div>
                    <div style={{display: 'flex', gap: '20px'}}>
                      <div className='underline' onClick={this.generateDownloadLink.bind(this, fileType)}>Replace with new generated {fileType.toUpperCase()}</div>
                      <a
                        href={this.generatedDownloadLinks[fileType].link.text}
                        target='_blank'
                        rel='noreferrer'
                      >
                        See Current {fileType.toUpperCase()}
                      </a>
                      <div className='underline' onClick={() => this.generatedDownloadLinks[fileType].askForConfirm = false}>Cancel</div>
                    </div>
                  </Alert>
                }
                {
                  this.generatedDownloadLinks[fileType].showConfirmation &&
                    <Alert
                      showCloseButton
                      background={Theme.green}
                      onClose={() => this.generatedDownloadLinks[fileType].showConfirmation = false}
                    >
                      <div>{`The ${fileType.toUpperCase()} of the post was generated${isPresent(this.generatedDownloadLinks[fileType].linkType) && isPresent(this.generatedDownloadLinks[fileType].link) ? ` and added to \'${this.generatedDownloadLinks[fileType].linkType.title}\'` : ''}.`}</div>
                      <a href={this.generatedDownloadLinks[fileType].link.text}><i className='fa fa-download' /> Download {fileType.toUpperCase()}</a>
                    </Alert>
                }
                {
                  this.generatedDownloadLinks[fileType].showSlowWarning && !this.generatedDownloadLinks[fileType].showConfirmation &&
                    <Alert
                      showCloseButton
                      onClose={() => this.generatedDownloadLinks[fileType].showSlowWarning = false}
                    >
                      The {fileType.toUpperCase()} is being generated. In a few minutes, refresh the page and check the download links.
                    </Alert>
                }
              </React.Fragment>,
            )}
          </fieldset>
          <div className='backend-title' onClick={() => this.showAdvancedSettings = !this.showAdvancedSettings} style={{cursor: 'pointer'}}>
            {
              this.showAdvancedSettings ?
                <i className='fa fa-chevron-down' />
                :
                <i className='fa fa-chevron-right' />
            }
            Advanced Settings
          </div>
          {
            this.showAdvancedSettings &&
              <>
                {
                  isPresent(WorksheetStore.showData.campaign) && !AppUI.site.blockPostNotifications &&
                    <fieldset>
                      <legend>Email Settings
                        <InfoTooltip icon={WorksheetStore.showData.published ? 'lock' : 'unlock'}>
                          These cannot be edited once the post has been published.
                        </InfoTooltip>
                      </legend>
                      <InPlaceCheckbox
                        value={WorksheetStore.showData.skipNotification}
                        field='skip_notification'
                        model='worksheets'
                        id={WorksheetStore.showData.id}
                        afterChange={this.getWorksheet}
                        disabled={WorksheetStore.showData.published}
                      >
                        Do Not Notify Members on Publication
                      </InPlaceCheckbox>
                      {
                        WorksheetStore.showData.published && isPresent(WorksheetStore.showData.campaign?.campaignTypeId) &&
                          <div style={{display: 'flex', flexDirection: 'column'}}>
                            <Text fontSize='16px'>
                              {WorksheetStore.showData.campaign.campaignMessagesCount} email notifications were sent.
                            </Text>
                            <Text fontSize='16px'>
                              <Link
                                target='_blank'
                                className='underline'
                                to={{
                                  name: 'creator.campaigns.edit',
                                  params: {
                                    campaignId: WorksheetStore.showData.campaign.id,
                                    campaignTypeId: WorksheetStore.showData.campaign.campaignTypeId,
                                    groupId: AppUI.groupId,
                                  },
                                }}
                              >
                                See More Information
                              </Link>
                            </Text>
                          </div>
                      }
                      {
                        !WorksheetStore.showData.published &&
                        <>
                          <div>
                            <InPlaceCheckbox
                              id={this.campaign?.id}
                              value={this.campaign?.greetByName}
                              model='campaigns'
                              field='greet_by_name'
                              disabled={WorksheetStore.showData.published}
                            >
                              {`Add greeting before the body of the email: ${this.campaign?.greeting(AppUI.user)}`}
                            </InPlaceCheckbox>
                            <InPlaceText
                              id={this.campaign?.id}
                              defaultValue={this.campaign?.customGreeting}
                              model='campaigns'
                              field='custom_greeting'
                              placeholder='Custom greeting'
                              afterChange={val => this.campaign?.set('customGreeting', val)}
                              disabled={WorksheetStore.showData.published}
                              show
                            />
                          </div>
                          <legend style={{marginTop: '10px'}}>Email Body</legend>
                          <InPlaceText
                            smallRichText
                            defaultValue={this.campaign?.mailingBody}
                            model='campaigns'
                            field='mailing_body'
                            id={this.campaign?.id}
                            afterChange={this.getWorksheet}
                            maxHeight='200px'
                            scrollable
                            placeholder="If left empty, the post's description will be used as the email's body."
                            blockClickToEdit={WorksheetStore.showData.published}
                          />
                          <Row>
                            <GrayButton
                              type='button'
                              onClick={this.onSendEmailTest}
                              style={{
                                height: '48px',
                                textAlign: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                              }}
                            >
                              {!this.showEmailTestSpinner && 'Send Test Email'}
                              {this.showEmailTestSpinner && <Spinner background='gray' margin='0'/>}
                            </GrayButton>
                            {
                              AppUI.user.developer && isPresent(WorksheetStore.showData.campaign?.campaignTypeId) &&
                                <Link
                                  target='_blank'
                                  to={{
                                    name: 'creator.campaigns.edit',
                                    params: {
                                      campaignId: WorksheetStore.showData.campaign.id,
                                      campaignTypeId: WorksheetStore.showData.campaign.campaignTypeId,
                                      groupId: AppUI.groupId,
                                    },
                                  }}
                                >
                                  See Email Campaign
                                </Link>
                            }
                          </Row>
                          {
                            !this.showEmailTestSpinner &&
                              <BackEndExplainer>
                                {`Test emails will be sent to ${AppUI.user.email} and any BCC addresses if they exist.`}
                              </BackEndExplainer>
                          }
                          {
                            this.showEmailTestMessage &&
                            <div className='alert'>
                              An email has been sent to you. If you don't find it in your inbox, check your spam folder.
                            </div>
                          }
                          <Documentation>
                            <i className='fa fa-info-circle' />
                            <span> Use </span><code>%name%</code><span> as a placeholder to automatically insert the recipient's name in the greeting or email body.</span>
                          </Documentation>
                        </>
                      }
                    </fieldset>
                }
                <fieldset>
                  <legend>Grid Section</legend>
                  <GridItemSearch
                    onSubmit={this.onAddGridItem}
                  />
                  {
                    WorksheetStore.hasShowData &&
                      <table style={{marginTop: '10px'}} className='table-wrapper admin-table'>
                        <thead>
                          <tr>
                            <th width='50' />
                            <th>Name</th>
                            <th>Posts Count</th>
                            <th width='40'></th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            WorksheetStore.showData.gridItems.map(item => {
                              return (
                                <tr key={item.id}>
                                  <td>
                                    <Link
                                      to={{ name: 'creator.grid_items.edit', params: { gridItemId: item.id } }}
                                      className='on-click'
                                    >
                                      <i className='fa fa-chevron-right fa-block' />
                                    </Link>
                                  </td>
                                  <td>
                                    <div className='text'>
                                      {item.name}
                                    </div>
                                  </td>
                                  <td>
                                    <div className='text'>
                                      {item.worksheetsCount}
                                    </div>
                                  </td>
                                  <td>
                                    <DeleteButton
                                      onConfirm={() => this.onRemoveGridItem(item.id)}
                                      message='Remove this grid row?'
                                      className='fa-block right'
                                    />
                                  </td>
                                </tr>
                              );
                            })
                          }
                        </tbody>
                      </table>
                  }
                </fieldset>
                <fieldset>
                  <legend>
                    {`Custom Tabs ${isPresent(WorksheetStore.showData.customTabs) ? `(${WorksheetStore.showData.customTabs?.length})` : ''}`}
                    <InfoTooltip>
                      <div>Custom tabs are used to display additional content on the post.</div>
                      <div>For example, you could set up a custom tab to embed an outtakes video.</div>
                    </InfoTooltip>
                  </legend>
                  {
                    CustomTabStore.requestCounter === 0 && CustomTabStore.indexData.length === 0 &&
                      <BackEndExplainer>You haven't set up any <Link className='underline' target='_blank' to={{ name: 'creator.custom_tabs' }}>custom tabs</Link> yet.</BackEndExplainer>
                  }
                  {
                    CustomTabStore.requestCounter === 0 && CustomTabStore.indexData.length > 0 &&
                      <table className='table-wrapper admin-table'>
                      <thead>
                        <tr>
                          <th width='50px' />
                          <th>Tab</th>
                          <th>Visibility</th>
                          <th width='40px'/>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          CustomTabStore.indexData.map(customTab => {
                            return (
                              <tr key={customTab.id}>
                                <td
                                  data-tip
                                  data-for={`worksheet-custom-tab-${customTab.id}`}
                                >
                                  {
                                  isPresent(this.worksheetCustomTab(customTab.id)) &&
                                    <Link
                                      to={{ name: 'creator.worksheets_custom_tabs.edit', params: { worksheetId: this.props.params.worksheetId, worksheetCustomTabId: this.worksheetCustomTab(customTab.id).id } }}
                                    >
                                      <i className='fa fa-chevron-right fa-block' />
                                    </Link>
                                  }
                                  {
                                    isBlank(this.worksheetCustomTab(customTab.id)) &&
                                      <ReactTooltip
                                        place='right'
                                        type='dark'
                                        effect='solid'
                                        id={`worksheet-custom-tab-${customTab.id}`}
                                        class='custom-tooltip'
                                      >
                                        No custom tab created for this template.
                                      </ReactTooltip>
                                  }
                                </td>
                                <td><i className={`fa fa-${customTab.icon}`} /> {customTab.title}</td>
                                <td>{customTab.formattedMembershipTypeNames}</td>
                                <td>
                                  {
                                    isBlank(this.worksheetCustomTab(customTab.id)) &&
                                      <div
                                        key={customTab.id}
                                        onClick={() => this.createWorksheetCustomTab(customTab.id)}
                                        style={{cursor: 'pointer'}}
                                        data-tip
                                        data-for={`create-worksheet-custom-tab-${customTab.id}`}
                                      >
                                        <i className='fa fa-plus fa-block' />
                                        <ReactTooltip
                                          place='left'
                                          type='dark'
                                          effect='solid'
                                          id={`create-worksheet-custom-tab-${customTab.id}`}
                                          class='custom-tooltip'
                                        >
                                          Create a custom tab from this template.
                                        </ReactTooltip>
                                      </div>
                                  }
                                  {
                                    isPresent(this.worksheetCustomTab(customTab.id)) &&
                                      <DeleteButton
                                        onConfirm={() => this.onDestroyWorksheetCustomTab(this.worksheetCustomTab(customTab.id).id)}
                                        message='Remove this custom tab?'
                                        className='fa-block right'
                                      />
                                  }
                                </td>
                              </tr>
                            );
                          })
                        }
                      </tbody>
                      </table>
                  }
                  {
                    this.creatingCustomTab &&
                      <Spinner />
                  }
                  {
                    CustomTabStore.requestCounter === 0 && CustomTabStore.indexData.length > 0 &&
                      <BackEndExplainer compact>
                        <div>Select Custom Tab templates and add them to this post. You can set up templates in the <Link className='underline' target='_blank' to={{ name: 'creator.custom_tabs' }}>Custom Tab settings</Link>.</div>
                        <div>Only custom tabs that are not set to be displayed on all posts are available to be added to posts manually.</div>
                      </BackEndExplainer>
                  }
                </fieldset>
              </>
          }
        </div>
      </Stack>
    );
  }
}

export default WorksheetEdit;
